import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AccountService } from '../shared/services/account.service';
import { AppTranslationService } from '../app-translation.service';
import { SessionDataService } from '../shared/services/session-data.service';
import { AccountRecoveryUtil } from '../shared/utils/accountrecovery.utils';
import {
  classDisabled,
  classNone,
  PasswordInput,
  VueFormError,
  VuePasswordStrengthInputComponent,
  VueServerMessage,
  VueServerMessageType, VueValidationRules
} from '@vue/vueframework';
import { UpdatePassword } from '../shared/types/UpdatePassword';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-password-reset-update',
  standalone: false,
  styleUrls: [ './password-reset-update.component.css' ],
  templateUrl: './password-reset-update.component.html'
})
export class PasswordResetUpdateComponent implements OnInit {
  @ViewChild('passwordResetComponent', { static: false })
  public passwordResetComponent: VuePasswordStrengthInputComponent;

  public readonly ACCOUNT_RECOVERY_BUNDLE_NAME = 'AccountRecovery';
  public messages: VueServerMessage[] = [];
  public formError = new VueFormError();

  public validHash: boolean;

  public translations = {
    pageTitle: '',
    submitFormError: '',
    unknownError: ''
  };

  public updatePassword: UpdatePassword = {
    hash: '',
    id: '',
    password: ''
  };

  public passwordInput: PasswordInput = {
    isStrongPassword: false,
    newPassword: ''
  };

  /**
     * Required rules for the password strength.
     * We currently use default optional rules.
     */
  public requiredRules: VueValidationRules = {
    rules: [
      {
        class: classNone,
        label: '',
        labelKey: 'passwordRule.label.notIncludeUsername',
        order: 2
      },
      {
        class: classDisabled,
        label: '',
        labelKey: 'passwordRule.label.minimumLength',
        labelParams: [ 12 ],
        order: 1,
        regex: new RegExp('^.{12,}$')
      }
    ]
  };

  public constructor(
    private route: ActivatedRoute,
    private router: Router,
    private accountService: AccountService,
    private appTranslationService: AppTranslationService,
    private sessionDataService: SessionDataService,
    private accountRecoveryUtil: AccountRecoveryUtil
  ) {}

  public ngOnInit(): void {
    this.getTranslations();
    if (this.route.snapshot.data.passwordWindowResolve && this.route.snapshot.data.passwordWindowResolve.error) {
      this.validHash = false;
      this.handleError(this.route.snapshot.data.passwordWindowResolve.error);
    } else {
      this.validHash = true;
      this.updatePassword.id = this.route.snapshot.params.resetWindowId;
      this.updatePassword.hash = this.route.snapshot.params.resetWindowHash;
    }
  }

  public returnToLogin(): void {
    this.accountRecoveryUtil.navigateToLogin();
  }

  public submit(): void {
    this.messages.length = 0;
    const passwordForm = this.passwordResetComponent.passwordResetForm;
    if (passwordForm.form.valid &&
      this.passwordInput.isStrongPassword &&
      this.passwordResetComponent.passwordsMatch
    ) {
      this.updatePassword.password = this.passwordInput.newPassword;
      this.accountService.updatePassword(this.updatePassword, this.route.snapshot.params.clientCode).subscribe(
        () => {
          this.accountRecoveryUtil.navigateToLogin();
        },
        (error) => {
          this.handleError(error);
        });
    } else {
      if (!passwordForm.form.valid) {
        // find the first field in error
        const pageControls = Object.keys(passwordForm.form.controls);
        let i = 0;
        do {
          if (passwordForm.form.controls[ pageControls[ i ] ].invalid) {
            this.formError.fieldInError = pageControls[ i ];
          }
          i++;
        }
        while (this.formError.fieldInError === '');
      }
      this.messages.push(
        new VueServerMessage(VueServerMessageType.Error, this.translations.submitFormError, true, '1')
      );
      this.formError.invalidFormSubmit = true;
    }
  }

  public messageClicked($event: VueServerMessage): void {
    if ($event.id === 'passwordResetWindowNotFound') {
      if (this.sessionDataService.getUser()) {
        this.sessionDataService.getUser().userName = '';
        this.passwordInput.newPassword = '';
      }
      this.accountRecoveryUtil.navigateToRetrievePassword(this.route.snapshot.params.clientCode);
    }
  }

  /**
   * Handles default error scenarios from accountService endpoints
   * @param err The error response from accountService
   */
  private handleError(err: HttpErrorResponse): void {
    this.messages.length = 0;
    if (err.status === 400 || err.status === 404) {
      if (err.error[ 0 ].code === 'AccountServices.passwordContainsUserName') {
        this.passwordResetComponent.passwordContainsUsername = true;
        this.messages.push(
          new VueServerMessage(VueServerMessageType.Error, this.translations.submitFormError, true, '1')
        );
      } else if (err.error[ 0 ].code === 'AccountServices.invalidClient') {
        this.accountRecoveryUtil.navigateToNotFound();
      } else {
        const errorMessage = this.appTranslationService.getTranslationFromErrorMessage(err.error,
          this.translations.unknownError);
        if (err.error[ 0 ].code === 'AccountServices.passwordResetWindowNotFound') {
          this.messages.push(
            new VueServerMessage(
              VueServerMessageType.Error,
              errorMessage,
              this.validHash,
              'passwordResetWindowNotFound',
              true
            )
          );
        } else {
          this.messages.push(
            new VueServerMessage(VueServerMessageType.Error, errorMessage, true, '1')
          );
        }
      }
    } else {
      this.messages.push(
        new VueServerMessage(VueServerMessageType.Error, this.translations.unknownError, true, '1')
      );
    }
  }

  private getTranslations(): void {
    this.translations.pageTitle = this.appTranslationService.getTranslation(this.ACCOUNT_RECOVERY_BUNDLE_NAME,
      'PasswordResetUpdateComponent.PageTitle');
    this.translations.unknownError = this.appTranslationService.getTranslation(this.ACCOUNT_RECOVERY_BUNDLE_NAME,
      'error.UnknownError');
    this.translations.submitFormError = this.appTranslationService.getTranslation(this.ACCOUNT_RECOVERY_BUNDLE_NAME,
      'error.GenericFormError');
  }
}
